import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { Calculator, Trash2, Copy, Check, FileText, Table as TableIcon, Receipt, RotateCcw, Eye, EyeOff, Sparkles, Loader2, Scissors, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { deleteEstimate, updateEstimate, updateEstimateGuarded, createEstimate, fillEstimateDescriptions, auditEstimateDuplicates } from "../lib/api";
import QuoteView from "./QuoteView";
import ConcurrentSaveDialog from "./ConcurrentSaveDialog";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const fmt = (n) =>
    (Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const priceTypeLabel = (t) =>
    t === "per_length" ? "per length" : t === "per_item" ? "per item" : "fixed";

// Dark-theme style overrides for the markdown report. react-markdown + remark-gfm
// handles headings, bold, lists, and GitHub tables — we only tweak classes.
const mdComponents = {
    h1: ({ node, ...p }) => (
        <h1 className="text-xl font-bold text-white mt-5 mb-2" {...p} />
    ),
    h2: ({ node, ...p }) => (
        <h2 className="text-lg font-bold text-white mt-5 mb-2 pl-2 border-l-2 border-[#00A3FF]" {...p} />
    ),
    h3: ({ node, ...p }) => (
        <h3 className="text-sm font-bold text-white mt-4 mb-1.5 uppercase tracking-wide" {...p} />
    ),
    p: ({ node, ...p }) => (
        <p className="text-sm text-slate-200 leading-relaxed my-2" {...p} />
    ),
    ul: ({ node, ...p }) => (
        <ul className="list-disc ml-5 my-2 space-y-1 text-sm text-slate-200" {...p} />
    ),
    ol: ({ node, ...p }) => (
        <ol className="list-decimal ml-5 my-2 space-y-1 text-sm text-slate-200" {...p} />
    ),
    li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
    strong: ({ node, ...p }) => <strong className="text-white font-bold" {...p} />,
    em: ({ node, ...p }) => <em className="text-slate-100" {...p} />,
    code: ({ node, inline, ...p }) =>
        inline ? (
            <code
                className="font-mono bg-[#0A1128] text-[#00A3FF] px-1 rounded-sm text-[0.85em]"
                {...p}
            />
        ) : (
            <code
                className="block font-mono bg-[#0A1128] text-slate-200 p-3 rounded-sm text-[0.85em] overflow-x-auto my-2 border border-[#2A3655]"
                {...p}
            />
        ),
    hr: () => <hr className="my-4 border-[#2A3655]" />,
    // Tables
    table: ({ node, ...p }) => (
        <div className="my-3 overflow-x-auto rounded-sm border border-[#2A3655]">
            <table className="w-full text-xs border-collapse" {...p} />
        </div>
    ),
    thead: ({ node, ...p }) => (
        <thead className="bg-[#0A1128] text-[10px] uppercase tracking-wider text-slate-400 font-bold" {...p} />
    ),
    tbody: ({ node, ...p }) => <tbody {...p} />,
    tr: ({ node, ...p }) => (
        <tr className="border-b border-[#1C253C] last:border-0" {...p} />
    ),
    th: ({ node, ...p }) => (
        <th className="text-left px-2 py-1.5 font-bold" {...p} />
    ),
    td: ({ node, ...p }) => (
        <td className="px-2 py-1.5 text-slate-200 align-top" {...p} />
    ),
    blockquote: ({ node, ...p }) => (
        <blockquote
            className="border-l-2 border-amber-500/50 pl-3 my-2 text-slate-300 italic text-sm"
            {...p}
        />
    ),
    a: ({ node, ...p }) => (
        <a
            className="text-[#00A3FF] hover:text-white underline underline-offset-2"
            target="_blank"
            rel="noopener noreferrer"
            {...p}
        />
    ),
};

export default function EstimateResultDialog({
    open,
    onOpenChange,
    estimate,
    project,
    onDeleted,
    onUpdated,
    onCreated,
}) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [view, setView] = useState("report"); // "report" | "lines" | "quote"
    // Whether to render the soft-deleted (hidden) priced lines. Defaults
    // to false so the table stays compact; pill above the table toggles.
    const [showHiddenLines, setShowHiddenLines] = useState(false);

    // iter-83 — keep a local copy of the estimate so QuoteView edits
    // (line description / amount / profit margin / customer info /
    // attachments) round-trip into Reports + Priced Lines without
    // requiring a parent reload. Resyncs when the prop changes.
    const [localEstimate, setLocalEstimate] = useState(estimate);
    useEffect(() => {
        setLocalEstimate(estimate);
    }, [estimate?.id, estimate]);

    const hasReport = !!(localEstimate?.report && localEstimate.report.trim().length > 0);
    // Auto-select "lines" if the AI didn't produce a full report (old records,
    // or parse-failed runs). MUST be declared before the early return so
    // hooks are called in the same order every render.
    useEffect(() => {
        setView(hasReport ? "report" : "lines");
    }, [estimate?.id, hasReport]);

    // iter-110: one-time retroactive strip of AI-emitted ``**bold**``
    // markdown from persisted line names / notes. Older estimates were
    // saved with the literal asterisks so contractors saw
    // "**Sanitary Forcemain**" in the priced-lines view. Runs once per
    // opened estimate; declared above the early return so hook order is
    // stable. Also purges "bogus totals rows" (qty=0 with placeholder
    // name that landed in the DB before the parser was hardened).
    useEffect(() => {
        if (!localEstimate?.id) return;
        const raw = localEstimate.lines || [];
        if (!raw.length) return;
        const stripMd = (s) =>
            (s || "")
                .replace(/\*\*(.+?)\*\*/g, "$1")
                .replace(/__(.+?)__/g, "$1");
        const dirty = raw.some(
            (l) =>
                (l.name || "").includes("**")
                || (l.notes || "").includes("**")
                || (l.description || "").includes("**"),
        );
        const isBogus = (l) => {
            const qty = Number(l.quantity) || 0;
            const lt = Number(l.line_total) || 0;
            const name = String(l.name || "").toLowerCase().trim();
            const unit = String(l.unit || "").trim();
            if (qty > 0) return false;
            if (lt <= 0) return false;
            if (!name || ["line name", "-", "\u2014", "n/a", "tbd"].includes(name)) return true;
            if (!unit || unit === "-" || unit === "\u2014") return true;
            return [
                "subtotal", "grand total", "add profit", "profit",
                "markup", "tax", "hst", "gst", "contingency",
                "contract total", "total due", "total price",
                "quote total", "line total",
            ].some((kw) => name.includes(kw));
        };
        const hasBogus = raw.some(isBogus);
        if (!dirty && !hasBogus) return;
        const cleaned = raw
            .filter((l) => !isBogus(l))
            .map((l) => ({
                ...l,
                name: stripMd(l.name),
                notes: stripMd(l.notes),
                description: stripMd(l.description),
            }));
        updateEstimate(localEstimate.id, { lines: cleaned })
            .then((updated) => {
                setLocalEstimate(updated);
                try {
                    onUpdated?.(updated);
                } catch {
                    /* silent */
                }
            })
            .catch(() => {
                /* silent — user can still edit inline to remove manually */
            });
    }, [localEstimate?.id]);

    // iter-110: retry-with-strategy state. Declared above the early
    // return so hook order is stable across renders.
    const [retrying, setRetrying] = useState(null);
    const [retryStage, setRetryStage] = useState("");
    const [fillingDescriptions, setFillingDescriptions] = useState(false);
    // iter-216 — audit-duplicates state
    const [auditing, setAuditing] = useState(false);
    // iter-137 — concurrent-save conflict state. Populated when the
    // guarded save comes back 409 so we can render the diff modal.
    const [conflict, setConflict] = useState(null); // {detail, patch}

    if (!localEstimate || !estimate) return null;

    const handleDelete = async () => {
        try {
            await deleteEstimate(localEstimate.id);
            toast.success("Estimate deleted");
            onDeleted && onDeleted();
            setConfirmOpen(false);
        } catch {
            toast.error("Couldn't delete");
        }
    };

    // Save patch to backend AND mirror into local state. Used by
    // QuoteView for inline line / profit-margin / attachment edits.
    // iter-136 — sends the local `saves` counter so the backend can
    // reject the write with 409 if a teammate saved a newer version
    // in another tab.
    // iter-137 — on 409 we open the side-by-side diff dialog instead
    // of just toasting so the losing user can pick "overwrite" or
    // "discard".
    const handleEstimateSave = async (patch) => {
        try {
            const updated = await updateEstimateGuarded(
                localEstimate.id,
                patch,
                typeof localEstimate.saves === "number" ? localEstimate.saves : undefined,
            );
            setLocalEstimate(updated);
            try {
                onUpdated?.(updated);
            } catch {
                /* parent handler shouldn't break the save flow */
            }
            return updated;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const isConflict = err?.response?.status === 409 && detail?.code === "concurrent_save";
            if (isConflict) {
                // Refresh local state to the server's winning version, but
                // stash the user's attempted patch so the diff dialog can
                // show "yours vs theirs".
                if (typeof detail.current_saves === "number") {
                    setLocalEstimate((prev) =>
                        prev ? { ...prev, saves: detail.current_saves } : prev,
                    );
                }
                setConflict({ detail, patch });
                // Swallow the 409 — the dialog owns the retry UX. Rethrowing
                // would trip CRA's dev error overlay on every collision.
                return null;
            }
            toast.error(typeof detail === "string" ? detail : "Couldn't save");
            throw err;
        }
    };

    // iter-137 — "Overwrite with mine" from the diff dialog. Bumps the
    // local `saves` to the server's value first so the retry passes
    // the guard.
    const handleOverwriteAfterConflict = async () => {
        if (!conflict) return;
        const patch = conflict.patch;
        setConflict(null);
        try {
            const updated = await updateEstimateGuarded(
                localEstimate.id,
                patch,
                typeof localEstimate.saves === "number" ? localEstimate.saves : undefined,
            );
            setLocalEstimate(updated);
            onUpdated?.(updated);
            toast.success("Your version saved on top");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Couldn't overwrite");
        }
    };
    const handleDiscardAfterConflict = () => {
        setConflict(null);
        toast.message("Kept the other version. Your edits were discarded.");
    };
    const handleEstimateLocalUpdate = (patch) => {
        setLocalEstimate((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    // Optimistic line edit + persist. Powers the inline name/notes
    // editing and the per-line delete button in the priced-lines view.
    // Backend recomputes totals on save so the displayed Grand total
    // refreshes automatically.
    const persistLines = async (newLines) => {
        handleEstimateLocalUpdate({ lines: newLines });
        try {
            await handleEstimateSave({ lines: newLines });
        } catch {
            /* handleEstimateSave already toasts */
        }
    };
    const editLine = (idx, patch) => {
        const current = localEstimate?.lines || [];
        const next = current.map((l, i) => (i === idx ? { ...l, ...patch } : l));
        persistLines(next);
    };
    const toggleLineHidden = (idx) => {
        const current = localEstimate?.lines || [];
        const target = current[idx];
        if (!target) return;
        const willHide = !target.hidden;
        const next = current.map((l, i) =>
            i === idx ? { ...l, hidden: willHide } : l,
        );
        persistLines(next);
        toast.success(willHide ? "Line hidden from quote" : "Line restored");
    };
    const restoreAllHiddenLines = () => {
        const current = localEstimate?.lines || [];
        if (!current.some((l) => l.hidden)) return;
        const next = current.map((l) => ({ ...l, hidden: false }));
        persistLines(next);
        toast.success("All hidden lines restored");
    };
    const lineLocked = !!localEstimate?.locked;

    const strategyOptions = [
        { id: "cost", label: "Cost", blurb: "Straight cost — no markup, floor pricing" },
        { id: "competitive", label: "Competitive", blurb: "~10-20% below median — win-bid aggressive" },
        { id: "average", label: "Average", blurb: "Regional median — realistic market rate" },
    ];
    const handleRetryStrategy = async (strategy) => {
        if (retrying) return;
        setRetrying(strategy);
        setRetryStage("Queuing new estimate…");
        try {
            const payload = {
                project_id: localEstimate.project_id,
                template_id: localEstimate.template_id || null,
                template_ids: localEstimate.template_ids || null,
                all_templates: !!localEstimate.all_templates,
                document_ids: localEstimate.document_ids || null,
                extra_notes: localEstimate.extra_notes || "",
                scope: localEstimate.scope || "overall",
                trade: localEstimate.trade || null,
                trades: localEstimate.trades || null,
                plugin_ids: localEstimate.plugin_ids || null,
                plugin_prompt_ids: localEstimate.plugin_prompt_ids || null,
                plugin_adhoc_prompts: localEstimate.plugin_adhoc_prompts || null,
                plugin_prompt_inputs: localEstimate.plugin_prompt_inputs || null,
                profit_margin_pct: localEstimate.profit_margin_pct || 0,
                hide_profit_in_lines: !!localEstimate.hide_profit_in_lines,
                pricing_strategy: strategy,
                takeoff_items: localEstimate.takeoff_items || null,
            };
            const created = await createEstimate(payload, (s) => {
                setRetryStage(s?.stage || "Working…");
            });
            toast.success(
                `Re-priced with ${strategy.charAt(0).toUpperCase() + strategy.slice(1)} strategy`,
            );
            try {
                onCreated?.(created);
            } catch {
                /* upstream refresh handles it */
            }
            onOpenChange?.(false);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || err?.message || "Retry failed",
            );
        } finally {
            setRetrying(null);
            setRetryStage("");
        }
    };

    // iter-110: Retry with a different pricing strategy.
    const currentStrategy = (localEstimate?.pricing_strategy || "average").toLowerCase();

    // iter-110: Fill Descriptions on the current dialog.
    const handleFillDescriptionsDialog = async () => {
        if (!localEstimate?.id || fillingDescriptions) return;
        if (lineLocked) {
            toast.error("Unlock this bid first");
            return;
        }
        setFillingDescriptions(true);
        try {
            const updated = await fillEstimateDescriptions(localEstimate.id);
            setLocalEstimate(updated);
            try { onUpdated?.(updated); } catch { /* silent */ }
            toast.success("Descriptions filled");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't fill descriptions");
        } finally {
            setFillingDescriptions(false);
        }
    };

    // iter-216 — Audit duplicates. One-click fuzzy dedupe on the saved
    // estimate's priced lines, server-side. Produced by batched runs on
    // massive tenders where every chunk re-emits mobilization /
    // standards under slightly different wording.
    const handleAuditDuplicates = async () => {
        if (!localEstimate?.id || auditing) return;
        if (lineLocked) {
            toast.error("Unlock this bid first");
            return;
        }
        setAuditing(true);
        try {
            const result = await auditEstimateDuplicates(localEstimate.id);
            if (result.removed === 0) {
                toast.info(`No duplicates found across ${result.before} line items.`);
            } else {
                setLocalEstimate(result.estimate);
                try { onUpdated?.(result.estimate); } catch { /* silent */ }
                // iter-216 — count outlier flags so the user knows how many
                // consolidated rows they should eyeball before quoting.
                const outlierCount = (result.estimate?.lines || [])
                    .filter((l) => l?.price_outlier).length;
                const outlierSuffix = outlierCount > 0
                    ? ` · ${outlierCount} flagged for review (≥3× price spread)`
                    : "";
                toast.success(
                    `Collapsed ${result.removed} duplicate line${result.removed === 1 ? "" : "s"} ` +
                    `(${result.before} → ${result.after}). Median price used${outlierSuffix}.`,
                    { duration: 7000 },
                );
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't audit duplicates");
        } finally {
            setAuditing(false);
        }
    };

    const handleCopy = async () => {
        const toCopy = hasReport
            ? `${localEstimate.label}\n${"=".repeat(localEstimate.label.length)}\n\n${localEstimate.report}\n\nGRAND TOTAL: $${fmt(localEstimate.totals?.grand_total)}`
            : `${localEstimate.label}\n${"=".repeat(localEstimate.label.length)}\n\n` +
              (localEstimate.lines || [])
                  .map(
                      (ln) =>
                          `${ln.category} › ${ln.name}  —  ` +
                          `${fmt(ln.quantity)} ${ln.unit || ""} @ $${fmt(ln.unit_price)} ` +
                          `${priceTypeLabel(ln.price_type)}` +
                          ` → subtotal $${fmt(ln.subtotal)}` +
                          ` + add profit $${fmt(ln.markup_amount)}` +
                          ` = $${fmt(ln.line_total)}` +
                          (ln.notes ? `\n    (note: ${ln.notes})` : ""),
                  )
                  .join("\n") +
              `\n\nSubtotal:   $${fmt(localEstimate.totals?.subtotal)}` +
              `\nAdd profit: $${fmt(localEstimate.totals?.markup_total)}` +
              `\nGRAND TOTAL: $${fmt(localEstimate.totals?.grand_total)}`;
        try {
            await navigator.clipboard.writeText(toCopy);
            setCopied(true);
            toast.success("Estimate copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy");
        }
    };

    const lines = localEstimate.lines || [];
    const totals = localEstimate.totals || {};

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[1000px] max-h-[92vh] overflow-hidden p-4 sm:p-6"
                    data-testid="estimate-result-dialog"
                >
                    <DialogHeader className="min-w-0">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="text-white flex items-center gap-2">
                                    <Calculator className="w-5 h-5 text-[#00A3FF]" />
                                    <span className="truncate">{estimate.label}</span>
                                </DialogTitle>
                                <DialogDescription className="text-slate-400 mt-1">
                                    Priced from{" "}
                                    <span className="text-slate-200 font-medium">
                                        {estimate.template_name}
                                    </span>{" "}
                                    · {lines.length} line{lines.length === 1 ? "" : "s"}
                                    {(estimate.takeoff_item_ids || []).length > 0 && (
                                        <span
                                            data-testid="estimate-takeoff-badge"
                                            title="Measured takeoff quantities were used as ground truth for the matching line items."
                                            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#00C853]/15 border border-[#00C853]/40 text-[10px] uppercase tracking-wider font-bold text-[#00C853] align-middle"
                                        >
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11 5 7l-3 3 9 9L22 7l-3-3-10 10z"/></svg>
                                            {estimate.takeoff_item_ids.length} measured
                                        </span>
                                    )}
                                </DialogDescription>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                {/* iter-110: Retry with a different pricing
                                    strategy. Preserves the current estimate,
                                    creates a fresh sibling with the new
                                    strategy. */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            disabled={!!retrying || lineLocked}
                                            data-testid="estimate-retry-strategy-btn"
                                            aria-label="Retry with different pricing strategy"
                                            title={
                                                lineLocked
                                                    ? "Unlock estimate to re-price"
                                                    : "Re-run this estimate with a different pricing strategy"
                                            }
                                            className="flex items-center gap-1.5 p-2 rounded-sm text-slate-300 hover:bg-[#1C253C] hover:text-[#00A3FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {retrying ? (
                                                <Loader2 className="w-4 h-4 animate-spin text-[#00A3FF]" />
                                            ) : (
                                                <Sparkles className="w-4 h-4" />
                                            )}
                                            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider">
                                                {retrying ? "Re-pricing…" : "Re-price"}
                                            </span>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="bg-[#131A2F] border-[#2A3655] text-white w-[280px]"
                                        data-testid="estimate-retry-strategy-menu"
                                    >
                                        <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-widest">
                                            Re-run with pricing strategy
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator className="bg-[#2A3655]" />
                                        {strategyOptions.map((opt) => {
                                            const isCurrent = opt.id === currentStrategy;
                                            return (
                                                <DropdownMenuItem
                                                    key={opt.id}
                                                    disabled={isCurrent || !!retrying}
                                                    onSelect={(e) => {
                                                        e.preventDefault();
                                                        handleRetryStrategy(opt.id);
                                                    }}
                                                    data-testid={`estimate-retry-strategy-${opt.id}`}
                                                    className="flex-col items-start gap-0.5 hover:bg-[#1C253C] focus:bg-[#1C253C] cursor-pointer data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
                                                >
                                                    <div className="flex items-center gap-2 w-full">
                                                        <span className="font-semibold text-sm">
                                                            {opt.label}
                                                        </span>
                                                        {isCurrent && (
                                                            <span className="ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#00A3FF]/20 text-[#00A3FF] font-bold">
                                                                Current
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-[11px] text-slate-400 leading-tight">
                                                        {opt.blurb}
                                                    </span>
                                                </DropdownMenuItem>
                                            );
                                        })}
                                        {retryStage && (
                                            <>
                                                <DropdownMenuSeparator className="bg-[#2A3655]" />
                                                <div className="px-2 py-1.5 text-[11px] text-emerald-300 leading-tight">
                                                    {retryStage}
                                                </div>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <button
                                    type="button"
                                    onClick={handleFillDescriptionsDialog}
                                    disabled={fillingDescriptions || lineLocked}
                                    data-testid="estimate-fill-descriptions-btn"
                                    title={lineLocked ? "Unlock estimate to fill descriptions" : "Auto-fill the Description column on every line using AI"}
                                    className="flex items-center gap-1.5 p-2 rounded-sm text-slate-300 hover:bg-[#1C253C] hover:text-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {fillingDescriptions ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                                    ) : (
                                        <FileText className="w-4 h-4 text-emerald-400" />
                                    )}
                                    <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider">
                                        {fillingDescriptions ? "Filling…" : "Fill desc"}
                                    </span>
                                </button>
                                {/* iter-216 — Audit duplicates: collapses semantically-
                                    duplicate priced lines produced by the batched merge
                                    on massive tenders (every chunk re-emits mobilization,
                                    etc). One-click, no AI call, server-side. */}
                                <button
                                    type="button"
                                    onClick={handleAuditDuplicates}
                                    disabled={auditing || lineLocked || (lines?.length || 0) < 2}
                                    data-testid="estimate-audit-duplicates-btn"
                                    title={
                                        lineLocked
                                            ? "Unlock estimate to audit duplicates"
                                            : "Scan the priced lines and merge semantic duplicates (e.g. mobilization lines emitted multiple times across batched chunks). Keeps the median price per group."
                                    }
                                    className="flex items-center gap-1.5 p-2 rounded-sm text-slate-300 hover:bg-[#1C253C] hover:text-amber-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {auditing ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                                    ) : (
                                        <Scissors className="w-4 h-4 text-amber-400" />
                                    )}
                                    <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider">
                                        {auditing ? "Auditing…" : "Audit dupes"}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCopy}
                                    data-testid="estimate-copy-btn"
                                    className="p-2 rounded-sm text-slate-300 hover:bg-[#1C253C] hover:text-white transition-colors"
                                    aria-label="Copy as plain text"
                                >
                                    {copied ? (
                                        <Check className="w-4 h-4 text-green-400" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setConfirmOpen(true)}
                                    data-testid="estimate-delete-btn"
                                    className="p-2 rounded-sm text-slate-300 hover:bg-red-500/20 hover:text-red-300 transition-colors"
                                    aria-label="Delete estimate"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="overflow-y-auto max-h-[calc(92vh-9rem)] pr-1">
                        {estimate.summary && (
                            <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                                {estimate.summary}
                            </p>
                        )}

                        {/* View switcher — always visible so users can flip to Quote */}
                        <div
                            className="mb-3 inline-flex rounded-sm border border-[#2A3655] overflow-hidden"
                            data-testid="estimate-view-switcher"
                        >
                            {hasReport && (
                                <button
                                    onClick={() => setView("report")}
                                    data-testid="estimate-view-report"
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                                        view === "report"
                                            ? "bg-[#007AFF]/20 text-[#00A3FF]"
                                            : "text-slate-400 hover:text-white hover:bg-[#1C253C]"
                                    }`}
                                >
                                    <FileText className="w-3 h-3" />
                                    Full report
                                </button>
                            )}
                            <button
                                onClick={() => setView("lines")}
                                data-testid="estimate-view-lines"
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                                    hasReport ? "border-l border-[#2A3655]" : ""
                                } ${
                                    view === "lines"
                                        ? "bg-[#007AFF]/20 text-[#00A3FF]"
                                        : "text-slate-400 hover:text-white hover:bg-[#1C253C]"
                                }`}
                            >
                                <TableIcon className="w-3 h-3" />
                                Priced lines
                            </button>
                            <button
                                onClick={() => setView("quote")}
                                data-testid="estimate-view-quote"
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors border-l border-[#2A3655] ${
                                    view === "quote"
                                        ? "bg-[#007AFF]/20 text-[#00A3FF]"
                                        : "text-slate-400 hover:text-white hover:bg-[#1C253C]"
                                }`}
                            >
                                <Receipt className="w-3 h-3" />
                                Quote
                            </button>
                        </div>

                        {view === "quote" ? (
                            <QuoteView
                                estimate={localEstimate}
                                project={project}
                                onSave={handleEstimateSave}
                                onLocalUpdate={handleEstimateLocalUpdate}
                            />
                        ) : view === "report" && hasReport ? (
                            <div
                                className="prose prose-invert prose-sm max-w-none estimate-report"
                                data-testid="estimate-report"
                            >
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={mdComponents}
                                >
                                    {estimate.report}
                                </ReactMarkdown>
                            </div>
                        ) : (
                            <div className="rounded-sm border border-[#2A3655] overflow-hidden">
                                {(() => {
                                    const hiddenCount = lines.filter((l) => l.hidden).length;
                                    if (hiddenCount === 0) return null;
                                    return (
                                        <div
                                            className="flex items-center justify-between gap-3 px-3 py-2 bg-amber-500/10 border-b border-amber-500/30 text-xs"
                                            data-testid="estimate-hidden-banner"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setShowHiddenLines((s) => !s)}
                                                data-testid="estimate-hidden-toggle"
                                                className="flex items-center gap-1.5 font-medium text-amber-300 hover:text-amber-200"
                                            >
                                                {showHiddenLines ? (
                                                    <EyeOff className="w-3.5 h-3.5" />
                                                ) : (
                                                    <Eye className="w-3.5 h-3.5" />
                                                )}
                                                {hiddenCount} hidden line{hiddenCount === 1 ? "" : "s"}
                                                <span className="text-amber-400/70">
                                                    — {showHiddenLines ? "hide" : "show"}
                                                </span>
                                            </button>
                                            {!lineLocked && (
                                                <button
                                                    type="button"
                                                    onClick={restoreAllHiddenLines}
                                                    data-testid="estimate-restore-all-hidden"
                                                    className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 font-semibold"
                                                >
                                                    <RotateCcw className="w-3 h-3" />
                                                    Restore all
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()}
                                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#0A1128] border-b border-[#2A3655] text-[10px] uppercase tracking-wider font-bold text-slate-500">
                                    <div className="col-span-4 md:col-span-4">Item</div>
                                    <div className="col-span-2 md:col-span-2 text-right">Qty</div>
                                    <div className="hidden md:block md:col-span-2 text-right">Unit price</div>
                                    <div className="hidden md:block md:col-span-1 text-right">Add profit</div>
                                    <div className="col-span-5 md:col-span-2 text-right">Line total</div>
                                    <div className="col-span-1 md:col-span-1" />
                                </div>
                                {lines.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-slate-400">
                                        No priced lines were produced.
                                    </div>
                                ) : (
                                    lines.map((ln, i) => {
                                        if (ln.hidden && !showHiddenLines) return null;
                                        // iter-216 — Outlier flag: AI chunks disagreed by ≥3× on this scope.
                                        const outlier = ln.price_outlier && ln.price_range;
                                        return (
                                        <div
                                            key={i}
                                            className={`grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-[#1C253C] last:border-0 hover:bg-[#0F1B3A]/60 items-start ${ln.hidden ? "opacity-40 line-through" : ""} ${outlier ? "bg-amber-500/5 ring-1 ring-inset ring-amber-500/40" : ""}`}
                                            data-testid={`estimate-line-${i}`}
                                        >
                                            <div className="col-span-4 md:col-span-4 min-w-0">
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
                                                    <span>{ln.category}</span>
                                                    {outlier && (
                                                        <span
                                                            title={`Price outlier — the AI chunks disagreed by ${ln.price_range.ratio}× (${ln.price_range.count} chunks, $${Number(ln.price_range.min).toLocaleString()} → $${Number(ln.price_range.max).toLocaleString()}). The median was kept — please review before quoting the client.`}
                                                            data-testid={`estimate-line-${i}-outlier-badge`}
                                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[9px] font-bold normal-case tracking-normal cursor-help"
                                                        >
                                                            <AlertTriangle className="w-3 h-3" />
                                                            {ln.price_range.ratio}× SPREAD
                                                        </span>
                                                    )}
                                                </div>
                                                <input
                                                    type="text"
                                                    value={ln.name || ""}
                                                    onChange={(e) =>
                                                        handleEstimateLocalUpdate({
                                                            lines: localEstimate.lines.map((l, j) =>
                                                                j === i ? { ...l, name: e.target.value } : l,
                                                            ),
                                                        })
                                                    }
                                                    onBlur={(e) => editLine(i, { name: e.target.value })}
                                                    disabled={lineLocked}
                                                    data-testid={`estimate-line-${i}-name`}
                                                    placeholder="Line name"
                                                    className="w-full bg-transparent outline-none border-b border-transparent hover:border-[#2A3655] focus:border-[#00A3FF] text-sm text-white py-0 disabled:opacity-70 truncate"
                                                />
                                                <input
                                                    type="text"
                                                    value={ln.notes || ""}
                                                    onChange={(e) =>
                                                        handleEstimateLocalUpdate({
                                                            lines: localEstimate.lines.map((l, j) =>
                                                                j === i ? { ...l, notes: e.target.value } : l,
                                                            ),
                                                        })
                                                    }
                                                    onBlur={(e) => editLine(i, { notes: e.target.value })}
                                                    disabled={lineLocked}
                                                    data-testid={`estimate-line-${i}-notes`}
                                                    placeholder="Description / assumption"
                                                    className="w-full bg-transparent outline-none border-b border-transparent hover:border-[#2A3655] focus:border-[#00A3FF] text-[11px] text-slate-400 italic py-0 mt-0.5 disabled:opacity-70"
                                                />
                                            </div>
                                            <div className="col-span-2 md:col-span-2 text-right tabular-nums text-sm text-slate-200">
                                                {fmt(ln.quantity)}
                                                {ln.unit && (
                                                    <span className="text-slate-500 text-xs ml-1">
                                                        {ln.unit}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="hidden md:block md:col-span-2 text-right tabular-nums text-sm text-slate-200">
                                                ${fmt(ln.unit_price)}
                                                <span className="text-slate-500 text-[10px] ml-1">
                                                    {priceTypeLabel(ln.price_type)}
                                                </span>
                                            </div>
                                            <div className="hidden md:block md:col-span-1 text-right tabular-nums text-xs text-slate-400">
                                                ${fmt(ln.markup_amount)}
                                            </div>
                                            <div className="col-span-5 md:col-span-2 text-right tabular-nums font-semibold text-white">
                                                <span className="text-slate-500 mr-0.5">$</span>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={
                                                        typeof ln._amtDraft === "string"
                                                            ? ln._amtDraft
                                                            : fmt(ln.line_total)
                                                    }
                                                    onChange={(e) =>
                                                        handleEstimateLocalUpdate({
                                                            lines: localEstimate.lines.map((l, j) =>
                                                                j === i ? { ...l, _amtDraft: e.target.value } : l,
                                                            ),
                                                        })
                                                    }
                                                    onFocus={(e) => e.currentTarget.select()}
                                                    onBlur={(e) => {
                                                        const raw = String(e.target.value || "")
                                                            .replace(/[$,\s]/g, "");
                                                        const val = Number(raw);
                                                        // Strip the draft either way so the row re-renders
                                                        // from the persisted (or reverted) line_total.
                                                        const cleared = localEstimate.lines.map((l, j) => {
                                                            if (j !== i) return l;
                                                            const { _amtDraft: _drop, ...rest } = l;
                                                            return rest;
                                                        });
                                                        handleEstimateLocalUpdate({ lines: cleared });
                                                        if (!Number.isFinite(val) || val < 0) return;
                                                        if (Math.abs(val - (Number(ln.line_total) || 0)) < 0.005) return;
                                                        // Contractor override — collapse the row to a
                                                        // fixed lump so subtotal/grand-total pick up the
                                                        // new number without weird qty × unit_price math.
                                                        editLine(i, {
                                                            quantity: 1,
                                                            unit: ln.unit || "each",
                                                            unit_price: val,
                                                            price_type: "fixed",
                                                            markup_type: "percent",
                                                            markup_value: 0,
                                                            subtotal: val,
                                                            markup_amount: 0,
                                                            line_total: val,
                                                        });
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    disabled={lineLocked || ln.hidden}
                                                    data-testid={`estimate-line-${i}-total`}
                                                    className="w-24 text-right bg-transparent outline-none border-b border-transparent hover:border-[#2A3655] focus:border-[#00A3FF] py-0 tabular-nums font-semibold text-white disabled:opacity-70"
                                                    title={
                                                        lineLocked
                                                            ? "Unlock estimate to edit prices"
                                                            : "Type a new line total — overrides qty × unit price"
                                                    }
                                                />
                                            </div>
                                            <div className="col-span-1 md:col-span-1 flex items-center justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleLineHidden(i)}
                                                    disabled={lineLocked}
                                                    data-testid={`estimate-line-${i}-${ln.hidden ? "restore" : "hide"}`}
                                                    aria-label={ln.hidden ? "Restore line to quote" : "Hide line from quote"}
                                                    title={
                                                        lineLocked
                                                            ? "Unlock estimate to edit lines"
                                                            : ln.hidden
                                                              ? "Restore — add this line back into the quote"
                                                              : "Hide from quote — keeps it here but excludes it from the final quote"
                                                    }
                                                    className={`p-1.5 rounded-sm transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed ${
                                                        ln.hidden
                                                            ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                                            : "text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                                                    }`}
                                                >
                                                    {ln.hidden ? (
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                    ) : (
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {/* Totals (hidden on Quote view — quote has its own) */}
                        {view !== "quote" && (
                        <div className="mt-3 flex justify-end">
                            <div className="w-full md:w-80 bg-[#0F1B3A]/60 border border-[#2A3655] rounded-sm divide-y divide-[#1C253C]">
                                <Row label="Subtotal" value={totals.subtotal} />
                                <Row label="Add profit" value={totals.markup_total} />
                                <Row
                                    label="Grand total"
                                    value={totals.grand_total}
                                    emphasis
                                    testId="estimate-grand-total"
                                />
                            </div>
                        </div>
                        )}

                        {view !== "quote" && estimate.missing_items?.length > 0 && (
                            <div
                                className="mt-5 rounded-sm border border-amber-500/30 bg-amber-500/5 p-3"
                                data-testid="estimate-missing-items"
                            >
                                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 mb-1">
                                    Not in your template
                                </div>
                                <ul className="text-sm text-slate-200 list-disc list-inside space-y-1">
                                    {estimate.missing_items.map((m, i) => (
                                        <li key={i}>{m}</li>
                                    ))}
                                </ul>
                                <p className="text-[11px] text-amber-300/80 mt-2">
                                    Add these items to your template so the next estimate can price them.
                                </p>
                            </div>
                        )}

                        {view !== "quote" && estimate.questions?.length > 0 && (
                            <div className="mt-3 rounded-sm border border-[#2A3655] bg-[#0A1128] p-3">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-[#00A3FF] mb-1">
                                    Open questions
                                </div>
                                <ul className="text-sm text-slate-200 list-disc list-inside space-y-1">
                                    {estimate.questions.map((q, i) => (
                                        <li key={i}>{q}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent className="bg-[#131A2F] border border-[#2A3655] text-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this estimate?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-[#2A3655] text-slate-300 hover:bg-[#1C253C] hover:text-white">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-500 text-white"
                            data-testid="estimate-delete-confirm"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* iter-137 — concurrent-save conflict resolver. Mounted at
                the fragment root so it overlays the estimate dialog. */}
            <ConcurrentSaveDialog
                open={!!conflict}
                onOpenChange={(v) => !v && setConflict(null)}
                detail={conflict?.detail}
                localPatch={conflict?.patch}
                serverEstimate={localEstimate}
                onOverwrite={handleOverwriteAfterConflict}
                onDiscard={handleDiscardAfterConflict}
            />
        </>
    );
}

function Row({ label, value, emphasis = false, testId }) {
    return (
        <div
            className={`flex items-center justify-between px-3 py-2 ${
                emphasis ? "bg-[#007AFF]/10" : ""
            }`}
            data-testid={testId}
        >
            <span
                className={`text-xs uppercase tracking-wider font-bold ${
                    emphasis ? "text-white" : "text-slate-400"
                }`}
            >
                {label}
            </span>
            <span
                className={`tabular-nums ${
                    emphasis ? "text-xl font-bold text-white" : "text-sm text-slate-200"
                }`}
            >
                ${fmt(value)}
            </span>
        </div>
    );
}
